/**
 * Package @atlassian/invite-people lives in Atlassian Frontend repo
 * https://bitbucket.org/atlassian/atlassian-frontend/src/master/packages/people-and-teams/invite-people/
 * */

import React from 'react';
import InvitePeopleDrawer from '@atlassian/invite-people/InvitePeopleDrawer';
import InvitePeopleModal from '@atlassian/invite-people/InvitePeopleModal';
import type { InvitePeopleProps } from '@atlassian/invite-people/types';
import { useOrgId } from '@atlassian/jira-router-resources-navigation-org-id/src/index.tsx';
import { useTenantContext } from '@atlassian/jira-tenant-context-controller/src/components/tenant-context/index.tsx';
import { useProjectContext } from '@atlassian/jira-providers-project-context/src/index.tsx';
import { getSourceByInviteFlow } from '../../common/utils.tsx';
import { AnalyticsProvider } from '../../controllers/analytics/index.tsx';
import type { InviteFlow } from '../../controllers/types.tsx';
import { stripUrlToIssueKey, useInvitePeopleFlags, jiraProjectTypeToSubProduct } from './utils.tsx';

export type Props = {
	isOpen: boolean;
	inviteFlow: InviteFlow;
	continueUrl?: string;
	disableNoInviteError?: boolean;
	onClose: () => void;
	displayModal: boolean;
};

type Cohort = 'variation' | 'control';
const Content = ({
	isOpen,
	onClose,
	inviteFlow,
	continueUrl,
	disableNoInviteError,
	displayModal,
}: Props) => {
	const {
		cloudId,
		appEditions: { software },
	} = useTenantContext();
	const { data: orgId } = useOrgId();
	const { addFlag } = useInvitePeopleFlags();

	const { data: project } = useProjectContext();
	const subProduct = jiraProjectTypeToSubProduct(project?.projectType);

	const source = getSourceByInviteFlow(inviteFlow);

	const experimentalFeatures = {
		inviteFromAssignModal:
			// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
			'variation' as Cohort,
	};
	let continueUrlProp = continueUrl;

	if (continueUrl === undefined || continueUrl === null) {
		// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
		const issueKey = stripUrlToIssueKey(window.location.href) || null;

		// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
		continueUrlProp = issueKey ? `${window.location.origin}/browse/${issueKey}` : undefined;
	}

	const commonProps: InvitePeopleProps = {
		showFlag: addFlag,
		enableThirdParty: true,
		resourceAri: `ari:cloud:jira::site/${cloudId}`,
		subProduct,
		source,
		continueUrl: continueUrlProp,
		experimentalFeatures,
		disableNoInviteError,
		experimental_applicationEdition: software,
		orgId,
	};

	return (
		<AnalyticsProvider>
			{displayModal && isOpen ? (
				<InvitePeopleModal onCloseHandler={onClose} {...commonProps} />
			) : (
				<InvitePeopleDrawer isOpen={isOpen} onCloseHandler={onClose} {...commonProps} />
			)}
		</AnalyticsProvider>
	);
};

export default Content;
